'use client';

import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useLayoutControl } from 'src/context/layout';
import { useLinkToDuplicateCRMObjectMutation } from 'src/api/redux';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/pro-solid-svg-icons';
import { Dialog, PulseButton } from 'src/components/PulseUI';
import { exists } from 'src/utils/exists/exists';
import { sentenceCase } from 'src/utils/sentenceCase/sentenceCase';

interface PushToCRMDuplicateDialogProps {
  open: boolean;
  onClose: () => void;
}

const PushToCRMDuplicateDialog = ({
  open,
  onClose,
}: PushToCRMDuplicateDialogProps) => {
  const log = useLogging();
  const { setNotification } = useNotification();
  const { pushDuplicateToCRM } = useLayoutControl();
  const [linkToDuplicateCRMObject, { isLoading }] =
    useLinkToDuplicateCRMObjectMutation();

  const handleSubmit = async () => {
    try {
      await linkToDuplicateCRMObject({
        crm: pushDuplicateToCRM?.crm?.toLowerCase(),
        crm_id: pushDuplicateToCRM.crm_id,
        crm_type: pushDuplicateToCRM.crm_type,
        object_type: pushDuplicateToCRM.object_type,
        object_id: pushDuplicateToCRM.object_id,
        mapping: pushDuplicateToCRM.mapping,
      });
      setNotification({
        title: 'Success',
        message: 'Provider linked to existing CRM record',
        type: 'success',
      });
    } catch (err: any) {
      setNotification({
        title: 'Error',
        message: err?.error_message || 'Error linking to existing CRM record',
        type: 'error',
      });
      log.exception(`Error in PushToCRMDuplicateDialog: ${err}`);
    }

    log.event('Push Duplicate to CRM', {
      ...pushDuplicateToCRM,
    });

    onClose();
  };

  return (
    <Dialog
      isOpen={open}
      onClose={onClose}
      size="lg"
      className="!h-auto"
      showCloseButton
      data-testid="push-to-crm-duplicate-dialog"
    >
      <Dialog.Header>
        <h2
          className="text-lg font-semibold"
          data-testid="push-to-crm-duplicate-dialog-title"
        >
          Record Exists
        </h2>
      </Dialog.Header>
      <Dialog.Content className="p-6" data-testid="push-to-crm-duplicate-content">
        <p className="text-neutral-700" data-testid="duplicate-message">
          We found a {pushDuplicateToCRM?.crm_type} matching this provider in
          your CRM. Would you like to link this provider to the existing record?{' '}
          {exists(pushDuplicateToCRM?.link) && (
            <a
              href={pushDuplicateToCRM?.link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm items-center text-sky-600 hover:text-sky-700"
              aria-label={`View ${pushDuplicateToCRM?.crm_type} in ${sentenceCase(pushDuplicateToCRM?.crm)}`}
              data-testid="view-in-crm-link"
            >
              View {pushDuplicateToCRM?.crm_type} in{' '}
              {sentenceCase(pushDuplicateToCRM?.crm)}{' '}
              <Icon icon={faExternalLinkAlt} size="xs" />
            </a>
          )}
        </p>
      </Dialog.Content>
      <Dialog.Actions data-testid="push-to-crm-duplicate-actions">
        <PulseButton
          variant="outline"
          color="neutral"
          aria-label="Cancel"
          data-testid="duplicate-cancel-button"
          onClick={() => {
            onClose();
            log.event('Push Duplicate to CRM - Cancel', {
              ...pushDuplicateToCRM,
            });
          }}
        >
          Cancel
        </PulseButton>
        <PulseButton
          variant="solid"
          color="primary"
          loading={isLoading}
          onClick={handleSubmit}
          aria-label="Link to existing record"
          data-testid="duplicate-link-button"
        >
          Link to Existing
        </PulseButton>
      </Dialog.Actions>
    </Dialog>
  );
};

export default PushToCRMDuplicateDialog;
